import React, { useState, useEffect, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiLoader, FiCheck, FiX, FiUser, FiMail, FiCalendar, FiMessageSquare, FiAlertCircle } = FiIcons;

export default function CreatorApprovalTable() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('d2f_admin_access_requests')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (err) {
      console.error('[CreatorApprovalTable] Error fetching requests:', err);
      setError('Failed to load approval requests. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (requestId, requestEmail, displayName, role) => {
    setProcessingId(requestId);
    
    try {
      console.log('[CreatorApprovalTable] Starting approval for:', requestId);
      
      const { data, error } = await supabase.rpc('approve_creator_request', {
        p_request_id: requestId
      });

      if (error) {
        console.error('[CreatorApprovalTable] RPC error:', error);
        throw new Error(`Approval failed: ${error.message}`);
      }

      console.log('[CreatorApprovalTable] RPC success:', data);

      await fetchRequests();
      
      const message = data?.account_created 
        ? '✅ Application approved! Account created. Welcome email will be sent.'
        : '✅ Application approved! Welcome email sent.';
      
      alert(message);
    } catch (err) {
      console.error('[CreatorApprovalTable] Approval failed:', err);
      alert(`❌ Approval failed: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    if (reason === null) return;

    setProcessingId(requestId);
    try {
      const { error } = await supabase.rpc('reject_creator_request', {
        p_request_id: requestId,
        p_notes: reason || ''
      });

      if (error) throw error;
      await fetchRequests();
      alert('✅ Application rejected');
    } catch (err) {
      console.error('[CreatorApprovalTable] Rejection failed:', err);
      alert(`❌ Rejection failed: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-white">Creator Applications</h2>
        <button 
          onClick={fetchRequests}
          className="px-4 py-2 text-sm bg-neutral-800 hover:bg-neutral-700 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <SafeIcon icon={FiAlertCircle} />
          {error}
        </div>
      )}

      <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12 text-neutral-400">
            <SafeIcon icon={FiLoader} className="animate-spin text-2xl mr-3" />
            Loading applications...
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-neutral-400">
            <SafeIcon icon={FiUser} className="text-4xl mb-3" />
            <p>No pending applications</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-black/50 text-neutral-400">
                <tr>
                  <th className="px-6 py-4 font-medium">Applicant</th>
                  <th className="px-6 py-4 font-medium">Role</th>
                  <th className="px-6 py-4 font-medium">Applied</th>
                  <th className="px-6 py-4 font-medium">Reason</th>
                  <th className="px-6 py-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {requests.map(request => (
                  <tr key={request.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-neutral-800 flex items-center justify-center">
                          <SafeIcon icon={FiUser} className="text-neutral-500" />
                        </div>
                        <div>
                          <div className="font-medium text-white">{request.display_name || 'Anonymous'}</div>
                          <div className="text-xs text-neutral-500 flex items-center gap-1">
                            <SafeIcon icon={FiMail} className="text-xs" />
                            {request.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                        {request.requested_role ? request.requested_role.replace('_', ' ') : 'Artist'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-neutral-400">
                      <div className="flex items-center gap-1.5">
                        <SafeIcon icon={FiCalendar} className="text-xs" />
                        {new Date(request.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-neutral-400 max-w-xs truncate">
                      <div className="flex items-center gap-1.5">
                        <SafeIcon icon={FiMessageSquare} className="text-xs" />
                        {request.reason || 'No reason provided'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {processingId === request.id ? (
                          <SafeIcon icon={FiLoader} className="animate-spin text-neutral-400" />
                        ) : (
                          <>
                            <button
                              onClick={() => handleApprove(request.id, request.email, request.display_name, request.requested_role)}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20 transition-colors flex items-center gap-1.5"
                            >
                              <SafeIcon icon={FiCheck} />
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(request.id)}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors flex items-center gap-1.5"
                            >
                              <SafeIcon icon={FiX} />
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}